import en from "./earlyTranslations/en.json";
import fr from "./earlyTranslations/fr.json";
import es from "./earlyTranslations/es.json";
import pt from "./earlyTranslations/pt-br.json";
import zh from "./earlyTranslations/zh.json";
import { LocalesHelper } from "./localesHelper";

export const isLanguageSupported = (language: string, supportedLanguages: string[]): boolean =>
    supportedLanguages.includes(language);

/**
 * Get the language to use from the browser language according to the supported languages in the application.
 * Example 1:
 *  - The browser language is "en-US"
 *  - "en-US" is in supportedLanguages, language will be "en-US"
 * Example 2:
 *  - The browser language is "en-US"
 *  - "en-US" is NOT in supportedLanguages, but "en" is. Language will be "en"
 * Example 3:
 *  - The browser language is "en-US"
 *  - "en-US" is NOT in supportedLanguages, and "en" neither. Language will be fallbackLanguage.
 */
export const getLanguageFromSupported = (
    language: string,
    supportedLanguages: string[],
    fallbackLanguage: string
): string => {
    if (isLanguageSupported(language, supportedLanguages)) {
        return language;
    } else {
        const [languageOnly] = language.split("-");
        return isLanguageSupported(languageOnly, supportedLanguages) ? languageOnly : fallbackLanguage;
    }
};

// bundled at build time for messages shown before i18next initializes
const earlyTranslations: Record<string, Record<string, string>> = { en, fr, es, pt, zh };

// reads the user's language from localStorage (set by i18next) or falls back to browser language
const getEarlyLanguage = (): string => {
    if (typeof window === "undefined") return "en";
    const stored = localStorage.getItem("i18nextLng");
    return LocalesHelper.getLanguageCode(stored || navigator.language);
};

// cached once per page load since the language won't change during the loading phase
const earlyLanguage = getEarlyLanguage();

export const getEarlyTranslation = (key: string): string =>
    earlyTranslations[earlyLanguage]?.[key] ?? earlyTranslations.en[key] ?? key;
